import React, { useEffect, useState } from 'react';
import DateSelector from '../lib/DateSelector/DateSelector';
import { Modal } from '@flightstats/airframe-react';
import { modalContentStyle } from "../components/modal/Modal";

interface Field {
  name: string;
  label: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
}

interface WaiverModalProps {
  title: string;
  fields: Field[];
  size: string;
  submit: (result: { [key: string]: string }) => void;
  isModalOpen: boolean;
  onCancel: () => void;
  className?: string;
}

const WaiverModal: React.FC<WaiverModalProps> = ({ title, fields, size, onCancel, isModalOpen, submit, className }) => {
  const initializedResult: { [key: string]: string } = {};
  fields.forEach((field) => {
    initializedResult[field.name] = field.value;
  });
  const [result, setResult] = useState<{ [key: string]: string }>(initializedResult);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResult(initializedResult);
  }, [fields])
  const handleChange = (name: string, value: string | Date | null) => {
    setError(null);
    const stringValue = value instanceof Date ? value.toISOString().split('T')[0] : value || '';
    setResult({ ...result, [name]: stringValue });
  };
  const validateForm = (e: React.FormEvent) => {
    let invalid = false;
    fields.forEach((field) => {
      if (field.required && !result[field.name]) {
        e.preventDefault();
        e.stopPropagation();
        invalid = true;
        return;
      }
    });
    return invalid;
  }

  const handleCancel = () => {
    setResult(initializedResult);
    onCancel();
  }

  const ok = (e: React.FormEvent) => {
    const invalid = validateForm(e);
    if (invalid) {
      setError('Please fill all required fields.');
      return;
    }
    e.preventDefault();
    submit(result);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      const modal = document.querySelector('.issue-resolution-modal');
      if (!modal) return;

      const focusableElements = Array.from(
        modal.querySelectorAll(
          'input:not([readonly]), button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
        )
      ) as HTMLElement[];

      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const currentIndex = focusableElements.findIndex(
        (el) => el === document.activeElement
      );
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else {
          focusableElements[currentIndex - 1].focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        } else {
          focusableElements[currentIndex + 1].focus();
        }
      }
    }
  };

  return (
    <Modal isOpen={isModalOpen} contentStyle={modalContentStyle}>
      <div className={`issue-resolution-modal modal-dialog modal-${size} ${className ?? ''}`}>
        <form name="form" noValidate onSubmit={ok}>
          <div className="title">
            {title}
            <a onClick={handleCancel}>
              <i id="modalCancelled" className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
            </a>
          </div>

          <div className="content" onKeyDown={handleKeyDown}>
            <div className="choices">
              {fields.map((field, index) => (
                <div className="row field-sep" key={index}>
                  <div className="col-md-3 fs-label fs-label-xs hidden-sm hidden-xs">
                    {field.label}{field.required ? " *" : ""}
                  </div>
                  <div className="col-md-3 fs-label fs-label-xs visible-sm visible-xs">
                    {field.label}{field.required ? " *" : ""}
                  </div>
                  <div className="col-md-9">
                    {field.type !== 'date' ? (
                      <input
                        type="text"
                        placeholder={field.placeholder}
                        value={result[field.name] || ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        required={field.required}
                        autoFocus={index === 0}
                        className="form-control"
                        name={field.name}
                      />
                    ) : (
                      <DateSelector
                        includeTime={false}
                        value={field.value}
                        dateFormat="yyyy-MM-dd"
                        required={field.required}
                        onChange={(date) => handleChange(field.name, date)}
                        placeholderText='YYYY-MM-DD'
                      />
                    )}
                  </div>
                </div>
              ))}
              <div className="row field-sep">
                <div className="col-md-3"></div>
                <div className="col-md-9">
                  <div className="form-note-info">{error ? <span className='alert'>{error}</span> : "* Required"} </div>
                </div>
              </div>
            </div>

            <div className="action">
              <button id="modalSubmitted" className="btn btn-default" disabled={!result}>
                OK
              </button>
              <button id="modalCancelSubmitted" className="btn btn-default margin-left-5" onClick={handleCancel}>
                CANCEL
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default WaiverModal;